import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useCountryListContext } from "../../contexts/CountryListContext";
import {COUNTRY_CODE_CONSTANTS} from "../../constants/StringConstants";
import {
  ItemContainer,
  StarSpan,
  FormTitle,
  SubNavigationTitle,
  FieldLabelContainer,
  ErrorContainer,
  FormContainer,
  CustomizedCountrySelect,
  CustomisedPhoneInput,
  PhoneContainer,
  CustomisedPrimeInput,
  CustomisedBackPath,
  AddContactInfoWrapper,
  MandatoryFieldsContainer,
  FooterButtonWrapper,
} from "./InvoiceAddContact.styles";
import BackPath from "../../assets/icons/back-path.png";
import { CPButton, CustomisedTertiaryButton, FlexColumn, ModalBox, Wrapper } from "../../constants/common/StyledGlobalConstants";
import CPNotification from "../../pages/Login/CPNotification";
import { useTranslation } from "react-i18next";
import { TextWrapper } from "../ManagePrimaryContactInvoice.styles";
import { Line } from "@vds/lines";
import { ModalBody } from "@vds/modals";
import { Title } from "@vds/typography";
import { Loader } from "@vds/loaders";
import "../common/DropdownCustomStyles.css";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import PropTypes from "prop-types";

const NameRegex = /^[a-zA-Z\s]+$/;
const EmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validateEmailFormat = (email) => {
  if (!email || email.length > 129) return false;
  if (email.includes('..') || email.endsWith('.')) return false;
  
  const [localPart, domainPart] = email.split('@');
  if (!localPart || !domainPart || localPart.length > 63) return false;
  
  return EmailRegex.test(email);
};

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

const InvoiceAddContact = (props) => {
  const {
    errorNotification,
    onErrorNotificationClose,
    onSaveButtonClicked,
    firstNameRes,
    lastNameRes,
    emailRes,
    phoneRes,
    unSavedModal,
    backToBillingScreen,
    windowSize,
    load,
    isCountryListLoading
  } = props;

  const [firstName, setFirstName] = useState({
    showError: false,
    isValid: false,
    errorText: "",
    fnameValue: "",
  });
  const [lastName, setLastName] = useState({
    showError: false,
    isValid: false,
    errorText: "",
    lnameValue: "",
  });
  const [contactEmail, setContactEmail] = useState({
    showError: false,
    isValid: false,
    errorText: "",
    initialRender: true,
    inputAutoFocus: false,
    emailValue: "",
  });
  const [contactPhoneNumber, setContactPhoneNumber] = useState({
    showError: false,
    errorText: "",
    isDefault: true,
    phoneNumber: "",
  });
  const [selectedCountry, setSelectedCountry] = useState();
  
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

  const [t] = useTranslation("manageInvoiceTranslation");
  const { userLanguage } = useSelector((state) => state.ACCOUNT);
  
  const dutchAddContactNotification = (
    <>
      Vanaf de volgende factuurcyclus sturen we facturen voor het contractaccount naar dit e-mailadres. Deze wijziging is over maximaal 24 uur
      zichtbaar in <strong>Mijn Account.</strong>
    </>
  );

  const validateContactForm = (field, value) => {
    if (field === "firstname") {
      setFirstName((prevState) => {
        return {
          ...prevState,
          fnameValue: value,
        };
      });
      if (NameRegex.test(value)) {
        setFirstName((prevState) => {
          return {
            ...prevState,
            showError: false,
            isValid: true,
            errorText: "",
          };
        });
        firstNameRes(value);
      } else {
        setFirstName((prevState) => {
          return {
            ...prevState,
            showError: true,
            isValid: false,
            errorText: value === "" ? t("First name is required") : t("Invalid first name"),
          };
        });
      }
    } else if (field === "lastname") {
      setLastName((prevState) => {
        return {
          ...prevState,
          lnameValue: value,
        };
      });
      if (NameRegex.test(value)) {
        setLastName((prevState) => {
          return {
            ...prevState,
            showError: false,
            isValid: true,
            errorText: "",
          };
        });
        lastNameRes(value);
      } else {
        setLastName((prevState) => {
          return {
            ...prevState,
            showError: true,
            isValid: false,
            errorText: value === "" ? t("Last name is required") : t("Invalid last name"),
          };
        });
      }
    } else if (field === "contEmail") {
      setContactEmail((prevState) => {
        return {
          ...prevState,
          emailValue: value,
        };
      });
      if (validateEmailFormat(value)) {
        setContactEmail((prevState) => {
          return {
            ...prevState,
            showError: false,
            isValid: true,
            errorText: "",
          };
        });
        emailRes(value);
      } else {
        setContactEmail((prevState) => {
          return {
            ...prevState,
            showError: true,
            isValid: false,
            errorText: value === "" ? t("Email is required") : t("Invalid email"),
          };
        });
      }
    }
  };

  const { accountUserTokenDetails } = useSelector((state) => state.ACCOUNT);
  const phoneUtil = PhoneNumberUtil.getInstance();
  const phoneInputField = useRef(null);

  const isPhoneValid = (phone) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      console.error("Phone number validation error:", error);
      return false;
    }
  };

  const { countryList, loading, error} = useCountryListContext();
 
useEffect(() => {
    const localSelectedCountry = accountUserTokenDetails?.ccode
      ? countryList.filter((res) => res.countryTwoLetterCode === accountUserTokenDetails?.ccode)
      : countryList;
      setSelectedCountry(localSelectedCountry?.[0] || null);
    
  }, [countryList]);

  useEffect(() => {
    contactPhoneNumber.inputAutoFocus &&
      setTimeout(() => {
        phoneInputField.current.focus();
        setContactPhoneNumber((prevState) => {
          return {
            ...prevState,
            inputAutoFocus: false,
          };
        });
      });
  }, [contactPhoneNumber.inputAutoFocus]);

  useEffect(() => {
    phoneRes("");
    if (!contactPhoneNumber.initialRender) {
      if (!contactPhoneNumber.phoneNumber) {
        setContactPhoneNumber((prevState) => {
          return {
            ...prevState,
            showError: false,
            isDefault: true,
            errorText: "",
          };
        });
      } else if (!isPhoneValid(`${selectedCountry?.countryCode}${contactPhoneNumber.phoneNumber}`)) {
        setContactPhoneNumber((prevState) => {
          return {
            ...prevState,
            showError: true,
            errorText: t("Invalid contact phone"),
            isDefault: false,
            isValid: false,
          };
        });
      } else {
        setContactPhoneNumber((prevState) => {
          return {
            ...prevState,
            showError: false,
            isValid: true,
          };
        });
        phoneRes(`${selectedCountry?.countryCode} ${contactPhoneNumber.phoneNumber}`);
      }
    }
  }, [contactPhoneNumber.phoneNumber, selectedCountry]);

  const toFormatPhoneNumber = (inputValue) => {
    const input = inputValue.replace(/\D/g, "").substring(0, 15);
    const areaCode = input.length === 8 ? input.substring(0, 1) : input.substring(0, 3);
    const middle = input.length === 8 ? input.substring(1, 4) : input.substring(3, 6);
    const last = input.length === 8 ? input.substring(4, 15) : input.substring(6, 15);

    return `${areaCode} ${middle} ${last}`;
  };

  const handlePhoneNumberChange = (event) => {
    setContactPhoneNumber((prevState) => {
      return {
        ...prevState,
        phoneNumber: event.target.value,
        initialRender: false,
      };
    });
  };

  const handlePhoneNumberOnBlur = () => {
    contactPhoneNumber.phoneNumber?.length > 0 &&
      setContactPhoneNumber((prevState) => {
        return {
          ...prevState,
          phoneNumber: toFormatPhoneNumber(contactPhoneNumber.phoneNumber),
        };
      });
  };

  const handleCountrySelectChange = (event) => {
    setSelectedCountry(event.value);
    setContactPhoneNumber((prevState) => {
      return {
        ...prevState,
        inputAutoFocus: true,
      };
    });
    setContactPhoneNumber((prevState) => {
      return {
        ...prevState,
        phoneNumber: "",
      };
    });
  };

  useEffect(() => {
    if (firstName.isValid && lastName.isValid && contactEmail.isValid && !contactPhoneNumber.showError) {
      setSaveButtonDisabled(false);
    } else {
      onErrorNotificationClose();
      setSaveButtonDisabled(true);
    }
  }, [firstName.isValid, lastName.isValid, contactEmail.isValid, contactPhoneNumber.showError]);

  const handleCancelClick = () => {
    if (!firstName.fnameValue && !lastName.lnameValue && !contactEmail.emailValue && !contactPhoneNumber.phoneNumber) {
      onErrorNotificationClose();
      backToBillingScreen(false);
    } else {
      unSavedModal(true);
    }
  };

  useEffect(() => {
    //Setting to default state when this popup modal is freshly open
    setFirstName({
      showError: false,
      isValid: false,
      errorText: "",
      fnameValue: "",
    });
    setLastName({
      showError: false,
      isValid: false,
      errorText: "",
      lnameValue: "",
    });
    setContactEmail({
      showError: false,
      isValid: false,
      errorText: "",
      initialRender: true,
      inputAutoFocus: false,
      emailValue: "",
    });
    setContactPhoneNumber({
      showError: false,
      errorText: "",
      isDefault: true,
      phoneNumber: "",
    });
    const localSelectedCountry_Reset = accountUserTokenDetails?.ccode
      ? countryList.filter((res) => res.countryTwoLetterCode === accountUserTokenDetails?.ccode)
      : countryList;
    setSelectedCountry(localSelectedCountry_Reset[0]);
  }, [!props.showContactModal]);
  const getPadding = (windowSize) => (windowSize >= DESKTOP ? "24px 0 24px 0" : "16px 0 16px 0");
  const getWidth = (windowSize) => (windowSize < DESKTOP ? "100%" : "60%");
  const calculateWidth = (windowSize) => (windowSize < TABLET ? "95.5%" : "100%");
  const getContactInfoContent = (userLanguage, dutchAddContactNotification, t) => {
    if (userLanguage === "nl-NL") {
      return <div>{dutchAddContactNotification}</div>;
    }
    return (
      <div>
        {t("Starting next billing cycle, we’ll send invoices for the contract account to this email address. This change will be visible in")}{" "}
        <strong>{t("My Account")}</strong>
        {t("in 24 hours or less.")}
      </div>
    );
  };
  const getMarginBottom = (windowSize) => (windowSize < DESKTOP ? "12px" : "24px");
  const getFooterWidth = (windowSize) => (windowSize < DESKTOP ? "100%" : "80%");
  const getFooterPadding = (windowSize) => (windowSize < TABLET ? "100%" : "auto");
  const getFooterMargin = (windowSize) => (windowSize < TABLET ? "16px" : "0");

  return (
      <ModalBox
        id="cp-modal"
        ariaLabel="Add Contact"
        opened={props.showContactModal}
        fullScreenDialog={true}
        closeButton={
          <>
            <Wrapper
              style={{
                display: "flex",
                padding: getPadding(windowSize),
                alignItems: "center",
              }}
              onClick={(event) => event.preventDefault()}
            >
              <CustomisedBackPath
                aria-label="back to invoice contracts"
                ariaLabel
                tabIndex="0"
                onKeyDown={(e) => (e.key === "Enter" || e.keyCode === 32) && handleCancelClick()}
                src={BackPath}
                onClick={() => handleCancelClick()}
                id="backarrow-add-invoice-contact"
              />
              <SubNavigationTitle desktop={windowSize >= DESKTOP}>
                <Title size="large">{t("Billing and payment")}</Title>
              </SubNavigationTitle>
            </Wrapper>
            {<Line type="xLight" inverted={false} />}
          </>
        }
        desktop={windowSize >= DESKTOP}
      >
        <ModalBody>
          <Wrapper display="flex" justifyContent="center" paddingLeft="12px" id="cp-modal-body">
            <Loader active={load || isCountryListLoading} fullscreen={true} />
            <FlexColumn width={getWidth(windowSize)}>
              <FormTitle desktop={windowSize >= DESKTOP}>
                <Title size="large">{t("Add recipient")}</Title>
              </FormTitle>
              <Wrapper paddingBottom="10px">
                <AddContactInfoWrapper ismobile={windowSize < DESKTOP}>
                  {getContactInfoContent(userLanguage, dutchAddContactNotification, t)}
                </AddContactInfoWrapper>
              </Wrapper>
              <MandatoryFieldsContainer desktop={windowSize >= TABLET}>
                <div>*- {t("Mandatory fields.")} </div>
              </MandatoryFieldsContainer>
              {errorNotification.show && (
                <Wrapper marginBottom={getMarginBottom(windowSize)}>
                  <CPNotification isOpen={errorNotification.show} notificationType="error" height="auto" width="fit-content" closeButton={false}>
                    <TextWrapper fontSize="14px" lineHeight="20px" fontWeight="400" marginTop="0" marginBottom="0">
                      {errorNotification.title}
                    </TextWrapper>
                  </CPNotification>
                </Wrapper>
              )}
              { error && !loading && (
                  <Wrapper marginBottom={getMarginBottom(windowSize)}>
                  <CPNotification isOpen={true} notificationType="error" height="auto" width="fit-content" closeButton={false}>
                    <TextWrapper fontSize="14px" lineHeight="20px" fontWeight="400" marginTop="0" marginBottom="0">
                    {(COUNTRY_CODE_CONSTANTS.COUNTRY_CODE_FETCH_ERROR)}
                    </TextWrapper>
                  </CPNotification>
                </Wrapper> )}
              <Wrapper padding={windowSize >= TABLET && "2rem 0"} width={calculateWidth(windowSize)}>
                <FormContainer windowSize={windowSize} desktop={windowSize >= TABLET} width="100%">
                  <ItemContainer desktop={windowSize >= TABLET}>
                    <FieldLabelContainer>
                      <label>{t("First name")}</label>
                      <StarSpan>*</StarSpan>
                    </FieldLabelContainer>
                    <div>
                      <CustomisedPrimeInput
                        required={firstName.showError}
                        validGreen={firstName.isValid}
                        name="firstName"
                        value={firstName.fnameValue}
                        aria-label="First Name"
                        placeholder={t("Enter first name")}
                        id="add-invoice-contact-firstname"
                        width={"100%"}
                        onChange={(e) => validateContactForm("firstname", e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {firstName.showError && <ErrorContainer>{firstName.errorText}</ErrorContainer>}
                  </ItemContainer>
                  <ItemContainer desktop={windowSize >= TABLET}>
                    <FieldLabelContainer desktop={windowSize >= TABLET}>
                      <label>{t("Last name")}</label>
                      <StarSpan>*</StarSpan>
                    </FieldLabelContainer>
                    <div>
                      <CustomisedPrimeInput
                        required={lastName.showError}
                        validGreen={lastName.isValid}
                        name="lastName"
                        value={lastName.lnameValue}
                        aria-label="Last Name"
                        placeholder={t("Enter last name")}
                        id="add-invoice-contact-lastname"
                        width={"100%"}
                        onChange={(e) => validateContactForm("lastname", e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {lastName.showError && <ErrorContainer>{lastName.errorText}</ErrorContainer>}
                  </ItemContainer>
                </FormContainer>
                <FormContainer windowSize={windowSize} desktop={windowSize >= TABLET} width="100%">
                  <ItemContainer desktop={windowSize >= TABLET}>
                    <FieldLabelContainer>
                      <label>{t("Email")}</label>
                      <StarSpan>*</StarSpan>
                    </FieldLabelContainer>
                    <div>
                      <CustomisedPrimeInput
                        required={contactEmail.showError}
                        validGreen={contactEmail.isValid}
                        name="contactEmail"
                        value={contactEmail.emailValue}
                        aria-label="Contact Email"
                        placeholder={t("Enter email address")}
                        id="add-invoice-contact-email"
                        width={"100%"}
                        onChange={(e) => validateContactForm("contEmail", e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {contactEmail.showError && <ErrorContainer>{contactEmail.errorText}</ErrorContainer>}
                  </ItemContainer>
                  <ItemContainer windowSize={windowSize} desktop={windowSize >= TABLET}>
                    <FieldLabelContainer desktop={windowSize >= TABLET}>
                      <label>{t("Contact phone")}</label>
                    </FieldLabelContainer>
                    <PhoneContainer>
                      <CustomizedCountrySelect
                        key={`country-select-${countryList.length}`}
                        value={selectedCountry}
                        onChange={handleCountrySelectChange}
                        options={countryList}
                        optionLabel="countryCodeWithName"
                        filter
                        resetFilterOnHide
                        valueTemplate={selectedCountry?.countryCode}
                        id="add-invoice-contact-countrycode"
                        tabIndex="0"
                        aria-label={t("add invoice contact country code")}
                      />
                      <CustomisedPhoneInput
                        placeholder={t("Enter phone")}
                        id="add-invoice-contact-phonenumber"
                        value={contactPhoneNumber.phoneNumber}
                        onChange={handlePhoneNumberChange}
                        onBlur={handlePhoneNumberOnBlur}
                        validPhoneNumber={contactPhoneNumber.isValid}
                        phoneNumberDefault={contactPhoneNumber.isDefault}
                        autoFocus={contactPhoneNumber.inputAutoFocus}
                        ref={phoneInputField}
                        customWidth={windowSize.width > 1060}
                        tabIndex="0"
                        aria-label={t("add invoice contact phone number")}
                      />
                    </PhoneContainer>
                    {contactPhoneNumber.showError && <ErrorContainer>{contactPhoneNumber.errorText}</ErrorContainer>}
                  </ItemContainer>
                </FormContainer>
              </Wrapper>
            </FlexColumn>
          </Wrapper>
        </ModalBody>
        <footer className="sticky-bottom-footer-add-contact">
          <Line type="xLight" inverted={false} />
          <FooterButtonWrapper width={getFooterWidth(windowSize)}>
            <CustomisedTertiaryButton
              ariaLabel="Cancel"
              onClick={() => handleCancelClick()}
              buttonWidth={getFooterPadding(props.windowSize)}
              id="add-invoice-contact-cancel"
            >
              {t("CANCEL")}
            </CustomisedTertiaryButton>
            <CPButton
              width={getFooterPadding(props.windowSize)}
              buttonHeight={"40px"}
              boxButton={true}
              disabled={saveButtonDisabled}
              onClick={() => onSaveButtonClicked()}
              ariaLabel="Save"
              marginLeft="24px"
              fontPaddingLeft="0.75rem"
              fontPaddingRight="0.75rem"
              id="add-invoice-contact-submit"
              style={{
                height: "2.5rem",
                marginRight: getFooterMargin(props.windowSize),
              }}
            >
              {t("SUBMIT")}
            </CPButton>
          </FooterButtonWrapper>
        </footer>
      </ModalBox>
  );
};


InvoiceAddContact.propTypes = {
  errorNotification: PropTypes.shape({
    show: PropTypes.bool,
    title: PropTypes.string,
  }),
  onErrorNotificationClose: PropTypes.func,
  onSaveButtonClicked: PropTypes.func,
  firstNameRes: PropTypes.func,
  lastNameRes: PropTypes.func,
  emailRes: PropTypes.func,
  phoneRes: PropTypes.func,
  unSavedModal: PropTypes.func,
  backToBillingScreen: PropTypes.func,
  windowSize: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.object,
  ]),
  load: PropTypes.bool,
  isCountryListLoading: PropTypes.bool,
  showContactModal: PropTypes.bool,
};

export default InvoiceAddContact;
